<template>
  <div class="tw-relative tw-grid tw-grid-cols-6 tw-gap-2">
    <div class="tw-col-span-4 tw-flex tw-items-center">
      <div
        class="tw-relative tw-h-3.5 tw-w-full tw-rounded-md"
        :class="bgVariant"
      >
        <div
          class="tw-h-3.5 tw-w-full tw-rounded-md"
          :class="overlayVariant"
          :style="width"
        ></div>
      </div>
    </div>
    <div v-if="showNumbers" class="tw-col-span-2 tw-flex tw-items-center">
      {{ `${completed}/${total}` }}
    </div>
    <slot
      v-if="isTextSlotVisible"
      name="text"
      class="tw-col-span-2 tw-flex tw-items-center"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs, useSlots } from "vue";

export interface ProgressBarProps {
  total: number;
  completed: number;
  showNumbers?: boolean;
}

const props = withDefaults(defineProps<ProgressBarProps>(), {
  total: 0,
  completed: 0,
  showNumbers: true,
});
const { total, completed, showNumbers } = toRefs(props);
const percentage = computed(() => (completed.value / total.value) * 100);

const bgVariants = {
  green: "tw-bg-system-success-100",
  yellow: "tw-bg-system-warning-100",
  red: "tw-bg-system-error-100",
};
const overlayVariants = {
  green: "tw-bg-system-success-400",
  yellow: "tw-bg-system-warning-400",
  red: "tw-bg-system-error-400",
};

const bgVariant = computed(() => {
  if (total.value === 0) {
    return bgVariants.red;
  }

  if (percentage.value > 70) {
    return bgVariants.green;
  }

  if (percentage.value < 70 && percentage.value > 35) {
    return bgVariants.yellow;
  }

  return bgVariants.red;
});

const overlayVariant = computed(() => {
  if (total.value === 0) {
    return bgVariants.red;
  }

  if (percentage.value > 70) {
    return overlayVariants.green;
  }

  if (percentage.value < 70 && percentage.value > 35) {
    return overlayVariants.yellow;
  }

  return overlayVariants.red;
});

const width = computed(() => {
  if (total.value === 0) {
    return "width: 0%";
  }

  return `width: ${percentage.value}%`;
});

const slots = useSlots();
const isTextSlotVisible = computed(() => slots["text"] && !props.showNumbers);
</script>
